/**
 * Shown when the helpdesk has nothing for this user.
 *
 * Reachable in normal operation, not only on misconfiguration: every article can
 * require feature flags, so a customer whose areas have no pages yet gets here.
 * A blank page would read as broken, so it points at what does work.
 *
 * The agent is offered by opening the side panel, the way the rest of the app
 * does, rather than by navigating to /chat — that route needs `chat.use`, and an
 * empty state must not hand someone a button into a permission error.
 */

import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useLayoutStore } from "@/store/layoutStore"
import { URLS } from "@/utils/urls.const"
import { IconMail, IconMessageCircle, IconPencil } from "@tabler/icons-react"
import { Link } from "react-router"
import { HELP_MANAGE } from "../routes"

/** Whether this reader can be offered the agent, and the way to open it. */
export function useAskAgent(): { canAskAgent: boolean; openAgent: () => void } {
  const { hasPermission } = useUser()
  const openAgent = useLayoutStore((state) => state.openChatPanel)
  const sidechatEnabled = useFeatureFlag("ENABLE_SIDECHAT")
  return {
    canAskAgent: sidechatEnabled && hasPermission(PERMISSIONS.CHAT_USE),
    openAgent,
  }
}

export function HelpdeskEmptyState() {
  const { translate } = useTranslation()
  const { canAskAgent, openAgent } = useAskAgent()
  // An empty helpdesk is the state staff most need the tools from.
  const canEdit = useFeatureFlag("HELPDESK_EDIT")

  return (
    <div className="flex h-full items-center justify-center px-XXL py-XL">
      <div className="max-w-md text-center">
        <Typography as="h1" variant="titleM" className="pb-S">
          {translate("HELPDESK/EMPTY_TITLE")}
        </Typography>
        <Typography as="p" variant="textS" className="pb-L text-grey5">
          {translate("HELPDESK/EMPTY_BODY")}
        </Typography>
        <div className="flex items-center justify-center gap-S">
          {canAskAgent ? (
            <Button size="sm" onClick={openAgent}>
              <IconMessageCircle size={15} stroke={1.75} />
              {translate("HELPDESK/EMPTY_ASK_AGENT")}
            </Button>
          ) : null}
          <Button asChild size="sm" variant="outline">
            <a href={URLS.INFO_BOX.HELP}>
              <IconMail size={15} stroke={1.75} />
              {translate("HELPDESK/CONTACT_US")}
            </a>
          </Button>
          {canEdit ? (
            <Button asChild size="sm" variant="outline">
              <Link to={HELP_MANAGE}>
                <IconPencil size={15} stroke={1.75} />
                {translate("HELPDESK/MANAGE_TITLE")}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
